import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { AnalyticsService } from '@frk/eds-components';
import { WINDOW } from '@ng-web-apis/common';
import { CaveatService } from '@products/caveats/services/caveat.service';
import {
  AppStateService,
  IUserProfile,
  IUserProfileInfo,
  ProfileService,
  WidenService,
  WindowScrollService,
} from '@services';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import {
  BannerContent,
  Link,
  LinkCollection,
  Video,
  WidenDocumentJson,
} from '@types';
import { Logger } from '@utils/logger';
import { take, takeUntil } from 'rxjs/operators';
import { LinkService } from '@services/link.service';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { TranslateService } from '@shared/translate/translate.service';
import { ViewModeService } from '@services/view-mode.service';

interface LinkListItem {
  description: string;
  link: {
    linkCollection: LinkCollection[];
  };
  image: {
    widenDocument: string;
  };
}
interface LinkListContent {
  title: string;
  summary: {
    content: string;
  };
  linkListItem: LinkListItem[];
}

interface NavLink {
  text: string;
  href: string;
  isNewWindow?: boolean;
  externalLink?: string;
  description?: string;
  signInRequired?: boolean;
}
/**
 * Logger
 */
const logger = Logger.getLogger('BannerComponent');
/**
 * FT Banner component
 *
 * This component is used at the top of a template and has it's own associated document type.
 *
 * Selector: `ft-banner`
 *
 * Exported as: `BannerComponent`
 */
@Component({
  selector: 'ft-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  /**
   * Banner Content
   */
  content: BannerContent;

  /**
   * Show Default content
   */
  showDefaultContent: boolean;

  /**
   * Skin type for VI banners
   */
  skin: string;

  /**
   * For analytics event tracking
   */
  event = 'callout_click';
  eventType = 'banner';
  eventName: string;
  linkUrl: string;
  linkText: string;
  buttonWidenParams: WidenDocumentJson;

  disclaimerCaveat: string;
  ribbon: boolean;
  /**
   * Object to be passed to EDS component
   */
  video: Video;
  gradient: string;

  /**
   * For button style and display label
   */
  bannerBtnLabel: string;
  bannerBtnStyle: string;
  layout: boolean;
  gradientLayout: boolean;
  thematic: boolean;
  thematicB: boolean;
  isHideTitle: boolean;
  signInRequired?: boolean;
  hideLockIcon?: boolean;
  enableSessionMask = false;
  showSimLogo: boolean;
  importantinformationlinkWidenParams: WidenDocumentJson;

  /**
   * greetingMessage pre fill data
   */
  greetingMessage: string;

  greetingName: string;
  /**
   * save profile pre fill data
   */
  prefillData: IUserProfileInfo;

  private unsubscribe$: Subject<void> = new Subject<void>();

  /**
   * Responsive Banner Properties
   */

  public isResponsiveImageVersion = false;
  public mobileSrcSet: string;
  public tabletSrcSet: string;
  public largeTabletSrcSet: string;
  public smallDesktopSrcSet: string;
  public desktopSrcSet: string;
  public links: NavLink[];

  // EDS

  /**
   * Constructor
   */
  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private appStateService: AppStateService,
    private widenService: WidenService,
    private analytics: AnalyticsService,
    private cd: ChangeDetectorRef,
    private caveatService: CaveatService,
    private scrollService: WindowScrollService,
    private ctaLinkService: LinkService,
    private profileService: ProfileService,
    private translateService: TranslateService,
    private viewModeService: ViewModeService
  ) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    super.ngOnInit();

    this.setBreadcrumbItems();
    // Set content on init
    this.content = this.getContent();
    this.buildQuickLink();
    this.skin = this.getVISkin();
    this.getDisclaimerCaveat();
    this.gradient = this.getGradient();
    this.gradientLayout = this.getGradientLayout();
    this.isRibbonDisplayed();
    this.initVideo();

    // calling profile to get displayname
    this.profileService
      .getUserProfile$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((profile: IUserProfile) => {
        if (profile.isLoggedIn) {
          this.prefillData = profile?.profileInfo;
          this.getGreetingCopy(this.content);
          return !!profile?.profileInfo?.displayName;
        }
      });

    // WDE-5206: need to manually detect change while changing segments.
    this.cd.detectChanges();
  }

  getGreetingTime(currentTime): string {
    if (!currentTime || !currentTime.isValid()) {
      return 'Hello';
    }

    const splitAfternoon = 12; // 24hr time to split the afternoon
    const splitEvening = 17; // 24hr time to split the evening
    const currentHour = parseFloat(currentTime.format('HH'));

    if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
      // Between 12 PM and 5PM
      return this.translateService.instant('common.good-afternoon');
    } else if (currentHour >= splitEvening) {
      // Between 5PM and Midnight
      return this.translateService.instant('common.good-evening');
    }
    // Between dawn and noon
    return this.translateService.instant('common.good-morning');
  }

  getGreetingCopy(content: BannerContent): BannerContent {
    // adding current Time using dayjs.
    if (this.prefillData && this.displayWelcomeMsg()) {
      // WDE-3582: For above condition, mask the greeting message containing user name from being logged in data dog.
      this.enableSessionMask = true;

      const currentTime = dayjs();

      this.greetingMessage = this.getGreetingTime(currentTime);
      this.greetingName =
        this.prefillData?.displayName || this.prefillData?.firstName || '';
      if (this.greetingMessage) {
        if (this.prefillData?.userId !== 'bypassuser') {
          content.title = `${this.greetingMessage} ${this.greetingName} `;
        } else {
          content.title = `${this.greetingMessage}`;
        }
      }

      this.cd.detectChanges();

      return content;
    }
  }

  /**
   * Init Video
   */
  initVideo() {
    const videoContent = this.getContent();
    this.layout = this.component.getParameters()?.verticalLayout;
    this.thematic = this.component.getParameters()?.thematic;
    this.showSimLogo = this.component.getParameters()?.showSimLogo;
    this.thematicB =
      this.component.getParameters()?.thematicBanner === 'Layout A'
        ? false
        : true;
    this.isHideTitle = this.component.getParameters()?.hideTitle;
    if (videoContent?.video) {
      this.video = this.mapVideoData(videoContent);
    }
  }
  /**
   * Get content from document
   */
  getContent(): BannerContent {
    // Read the content from the resourceapi
    let content;
    content = this.document?.getData<BannerContent>();

    // Do we have a widen image?
    if (content?.widenImage) {
      const widenAssetStr = content.widenImage?.widenAsset;
      if (widenAssetStr) {
        const widenAsset = JSON.parse(widenAssetStr);
        content.imageSrc = this.widenService.getWidenImageVariantUrl(
          widenAsset?.url,
          'original',
          'webp'
        );
        /**
         * Additional Device specific images
         */
        if (content?.widenImage?.additionalMobileImages?.length > 0) {
          this.isResponsiveImageVersion = true;
          const additionalMobileImages =
            content?.widenImage.additionalMobileImages;
          const regEx = RegExp(/\-\w/gm);
          additionalMobileImages.forEach((additionalAssets) => {
            const processedAssetType = (
              str = additionalAssets?.mobileImageType
            ) => {
              // Convert string from 'small-tablet' to 'smallTablet'
              if (str.match(regEx)) {
                const processed = str?.replace(
                  regEx,
                  str.match(regEx)[0].toUpperCase()
                );
                return processed?.replace('-', '');
              }
              return str;
            };

            const additionalWidenAsset = JSON.parse(
              additionalAssets.mobileImageAsset.widenDocument
            );
            content[
              processedAssetType()
            ] = this.widenService.getWidenImageVariantUrl(
              additionalWidenAsset?.url,
              'original',
              'webp'
            );
          });
        }
        // END
      }
    }

    // Do we have a logo image?
    if (content?.logoImage) {
      const widenAssetStr = content.logoImage?.widenAsset;
      if (widenAssetStr) {
        const widenAsset = JSON.parse(widenAssetStr);
        content.logoLink = this.widenService.getWidenImageVariantUrl(
          widenAsset?.url,
          'original',
          'webp'
        );

        // If alt text is null use title
        content.altlogoText =
          content.logoImage?.altText || content.logoImage?.title;
      }
    }

    // if there is internal document link is available.
    if (content?.button) {
      for (const button of content.button) {
        if (button.linkCompound?.linkCollection?.length) {
          button.linkCompound = button.linkCompound.linkCollection[0];
          button.linkCompound.url = this.ctaLinkService.getHref(
            this.page,
            button.linkCompound,
            this.appStateService?.getHomePageUrl()
          );
          button.linkCompound.externalLink = this.getExternalLink(
            button.linkCompound?.external,
            button.linkCompound?.externalSuffix
          );

          button.signInRequired = button.linkCompound?.signInLink;
          button.hideLockIcon = button.linkCompound?.hideLockIcon;
          const widenParams = this.ctaLinkService.getWidenDocumentParams(
            button.linkCompound.widenAssetCompound?.widenDocument
          );
          this.buttonWidenParams = widenParams;
          button.dataDocId = widenParams?.id.shift();
          button.noindex = widenParams?.noindex;
          button.dataTitle = widenParams?.document_title;
          button.externalId = widenParams?.external_id;

          // content required for analytic tracking
          this.linkText = button.linkCompound.displayText;
          this.eventName = content.title;
          this.linkUrl = button.linkCompound.url;
          this.signInRequired = button.signInRequired;
          this.hideLockIcon = button.hideLockIcon;
        }
      }
    }

    // if there is important information link available for standard banner
    if (content?.importantinformationlink?.linkCollection?.length) {
      content.importantinformationlink =
        content.importantinformationlink.linkCollection[0];
      content.importantinformationlink.url = this.ctaLinkService.getHref(
        this.page,
        content.importantinformationlink,
        this.appStateService?.getHomePageUrl()
      );

      if (content.importantinformationlink?.widenAssetCompound) {
        this.importantinformationlinkWidenParams = this.ctaLinkService.getWidenDocumentParams(
          content.importantinformationlink?.widenAssetCompound?.widenDocument
        );
      }
    }

    return content;
  }

  /**
   * Wayfinder button click event def.
   */
  wayFinderClicked() {
    this.scrollService.scrollToAnchor('wayfinder');
  }
  /**
   * Set external link class
   * @param external - is link external true|false
   * @param externalSuffix - suffix to add to external link
   */
  private getExternalLink(external: boolean, externalSuffix: string): string {
    if (external) {
      const externalLink = 'external-link';
      if (externalSuffix) {
        // Adding external link suffix
        return externalLink + '--' + externalSuffix;
      }
      return externalLink;
    }
  }

  openLink(inputLink: string) {
    this.windowRef.open(inputLink, '_blank');
  }

  /**
   * Get banner type (theme)
   */
  get type() {
    return this.component.getParameters().type;
  }

  /**
   * Get banner - is condensed info
   */
  get isCondensed() {
    return this.component.getParameters().isCondensed;
  }

  /**
   * Get banner title size for intorduction title.
   */
  get titleSize() {
    return this.component.getParameters()?.titleSize;
  }

  /**
   * get the disclaimer caveate text
   */
  private getDisclaimerCaveat() {
    if (this.displayCaveat()) {
      const caveatOverwriteText = this.getOverwriteCaveat()?.trim();
      if (caveatOverwriteText) {
        this.disclaimerCaveat = '<p>' + caveatOverwriteText + '</p>';
      } else {
        this.caveatService
          .getProximals$('BannerGlobalDisclaimer')
          .pipe(take(1))
          .subscribe((bannerCaveatContext) => {
            this.disclaimerCaveat = bannerCaveatContext[0];
            this.cd.detectChanges();
          });
      }
    }
  }

  /**
   * get skin type for VI banners
   */
  private getVISkin(): string {
    return this.component.getParameters()?.skin;
  }
  /**
   * @returns gradient or Image
   */
  private getGradient(): string {
    return this.component.getParameters()?.bannerBackground;
  }

  /**
   *
   * @returns col 8 or col 10 layout
   */
  private getGradientLayout(): boolean {
    if (this.component.getParameters()?.containerClass === 'container') {
      return true;
    }
    return false;
  }

  /**
   * Get the replacement text of caveat
   * @returns placement value as string
   */
  private getOverwriteCaveat(): string {
    return this.component.getParameters()?.overwriteCaveatText;
  }

  /**
   * Get the status if banner needs to display welcome message
   * @returns status in boolean
   */
  private displayWelcomeMsg(): boolean {
    return this.component.getParameters()?.displayWelcomeMsg;
  }

  /**
   * Get the status if banner needs to suppress caveat
   * @returns status in boolean
   */
  private displayCaveat(): boolean {
    return this.component.getParameters()?.displayCaveat;
  }
  /**
   * Get the status if banner needs Ribbon
   * @returns status in boolean
   */
  private isRibbonDisplayed() {
    this.ribbon = this.component.getParameters()?.displayRibbon;
  }
  /**
   * Get the Video Data
   */
  private mapVideoData(data) {
    return {
      accountId: data.video.accountId,
      videoId: data.video.videoId,
      playerId: data.video.videoPlayerId,
      mute: this.component.getParameters()?.mute,
      hideControls: this.component.getParameters()?.videoControls,
    };
  }
  /**
   * Track banner click event
   */
  private trackCtaClick(): void {
    this.analytics.trackEvent({
      event: this.event,
      detailed_event: 'Callout Clicks',
      event_data: {
        callout_type: this.eventType,
        category: '',
        link_id: '',
        link_text: this.linkText,
        link_type: this.analytics.getLinkType(this.linkUrl),
        link_url: this.linkUrl,
      },
      type: this.eventType,
      name: this.eventName,
      link_url: this.linkUrl,
      link_text: this.linkText,
    });
  }

  public processLink(event, linkCompound: Link): void {
    this.trackCtaClick();
    if (this.ctaLinkService.isLinkSignIn(linkCompound)) {
      this.ctaLinkService.openSignInLink(event, linkCompound);
    }
  }

  public buildQuickLink(): void {
    const { quickLinkDocument } = this.component?.getParameters();
    const content =
      quickLinkDocument &&
      this.page?.getContent(quickLinkDocument)?.getData<LinkListContent>();
    if (content) {
      this.links = this.getLinkItems(content);
    }
  }

  /**
   * Format Quick Links
   */
  public getLinkItems(content: LinkListContent): NavLink[] {
    const links = content?.linkListItem?.map((item: LinkListItem) => {
      const link: LinkCollection = item?.link?.linkCollection[0];
      const linkItem = {
        text: link.displayText,
        href: this.ctaLinkService.getCTALink(
          this.page,
          link,
          this.appStateService?.getHomePageUrl()
        ),
        isNewWindow: link?.target === '_blank' ? true : false,
        externalLink: this.getExternalLink(
          link?.external,
          link?.externalSuffix
        ),
        description: item.description,
        signInRequired: link.signInLink,
      };
      return linkItem;
    });
    return links;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
